import {
  type FilterField,
  type LibraryItem,
  primaryTopicPairOf,
  topicPairLabel,
} from './filter-engine'

export interface SortSpec {
  field: FilterField | 'plans' | 'source' | 'lastSeen'
  dir: 'asc' | 'desc'
}

export interface SortContext {
  /**
   * Effective external id per row — the session-picked source's id rather
   * than `item.extId` (LG-362 §3: the table sorts what it shows). Absent,
   * the stored id decides.
   */
  extIdOf?: (item: LibraryItem) => string | null
}

const difficultyOrder: Record<LibraryItem['difficulty'], number> = {
  Easy: 0,
  Med: 1,
  Hard: 2,
  '—': 3,
}

const sortValue = (
  item: LibraryItem,
  field: SortSpec['field'],
  context: SortContext,
): string | number | null => {
  if (field === 'plans' || field === 'plan') return item.plans.length
  if (field === 'elo') return item.elo
  if (field === 'difficulty') return difficultyOrder[item.difficulty]
  if (field === 'topic') {
    // The row's primary pair, so rows sharing a primary topic cluster
    // instead of interleaving on their second and third pairs. A row with no
    // primary pair sorts null, and the shared null handling parks it at the
    // end in both directions like an unknown ELO.
    //
    // Read off the marker, not off pair 0: a row whose first approach is
    // untagged still carries later pairs, and sorting it under the first of
    // those would file it beside rows that really are that topic.
    const primary = primaryTopicPairOf(item)
    return primary === null ? null : topicPairLabel(primary)
  }
  if (field === 'company') return item.companies.join(',')
  if (field === 'extId') {
    // Bare numeric IDs (persisted rows) must sort numerically — as strings,
    // "1000" would land before "215". Non-numeric IDs stay lexicographic;
    // missing IDs sort last via the shared null handling.
    const extId = context.extIdOf ? context.extIdOf(item) : item.extId
    if (extId === null) return null
    const numeric = Number(extId)
    return Number.isFinite(numeric) ? numeric : extId
  }
  if (field === 'source') return item.source ?? null
  // Unseen and dateless-seen rows have no date: the shared null handling
  // keeps them at the end in both directions, like unknown ELO.
  if (field === 'lastSeen') return item.lastSeenAt?.getTime() ?? null
  return item.title
}

const compareValues = (
  left: string | number,
  right: string | number,
): number => {
  if (typeof left === 'number' && typeof right === 'number') return left - right
  const leftText = String(left)
  const rightText = String(right)
  if (leftText < rightText) return -1
  if (leftText > rightText) return 1
  return 0
}

export function applySorts(
  sorts: SortSpec[],
  items: LibraryItem[],
  context: SortContext = {},
): LibraryItem[] {
  return items
    .map((item, originalIndex) => ({ item, originalIndex }))
    .sort((left, right) => {
      for (const sort of sorts) {
        const leftValue = sortValue(left.item, sort.field, context)
        const rightValue = sortValue(right.item, sort.field, context)
        // Missing metadata is unknown, not zero. Keep it at the end in both
        // directions so an ELO sort never promotes uncategorized content.
        if (leftValue === null || rightValue === null) {
          if (leftValue === rightValue) continue
          return leftValue === null ? 1 : -1
        }
        const comparison = compareValues(leftValue, rightValue)
        if (comparison !== 0) {
          return sort.dir === 'desc' ? -comparison : comparison
        }
      }
      return left.originalIndex - right.originalIndex
    })
    .map(({ item }) => item)
}

export function setPrimarySort(
  sorts: SortSpec[],
  field: SortSpec['field'],
): SortSpec[] {
  const primary = sorts[0]
  if (primary?.field === field) {
    return [
      { field, dir: primary.dir === 'asc' ? 'desc' : 'asc' },
      ...sorts.slice(1),
    ]
  }

  const dir: SortSpec['dir'] =
    field === 'elo' || field === 'plan' || field === 'plans' ? 'desc' : 'asc'
  return [{ field, dir }, ...sorts.filter((sort) => sort.field !== field)]
}
